import {getRetailLocationAdditionalColumns, type RetailLocationTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/retailLocationsWorkspaceCapability/domain/RetailLocationTemplate";
import {createCapabilityUdoColumnResolver, type CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {UserDefinedObjectTemplateAdditionalColumn} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {getEventAdditionalColumns, getEventContainerAdditionalColumns} from "~/utilities/capabilityUdoTemplateColumns";
import {createContext, useContext, useMemo} from "react";
import type {Workspace} from "@vesta/vos-types/modules/platformLevel/workspaces/domain/Workspace";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {ProductCatalogueTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogueTemplate";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {UserDetails} from "@vesta/vos-types/modules/platformLevel/userDetails/domain/UserDetails.ts";
import type {WorkspaceCapabilityAccessMatrix} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityAccessMatrix";
import type {UserDefinedObjectTemplate} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import type {UserDefinedObject} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObject";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {WorkspaceUiConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceUiConfig";
import type {DomainAggregateTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/domainAggregateWorkspaceCapability/domain/DomainAggregateTemplate";

type WorkspaceContextData = {
    workspace: Workspace;
    accessibleWorkspaces?: Array<Workspace>;
    workspaceCapabilities: Array<WorkspaceCapability<WorkspaceCapabilityType>>;
    workspaceUiConfig?: WorkspaceUiConfig;
    userTemplates: Array<UserTemplate>;
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
    productCatalogueTemplates: Array<ProductCatalogueTemplate>;
    eventContainerTemplates: Array<EventContainerTemplate>;
    eventTemplates: Array<OttopilotEventTemplate>;
    retailLocationTemplates: Array<RetailLocationTemplate>;
    domainAggregateTemplates: Array<DomainAggregateTemplate>;
    platformUserDetails: Array<UserDetails>;
    workspaceCapabilityAccessMatrix?: WorkspaceCapabilityAccessMatrix;
    userDefinedObjects: Array<UserDefinedObject>;
    userDefinedObjectTemplates: Array<UserDefinedObjectTemplate>;
    userDefinedObjectOptionsByTemplateId: ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>;
};

export const WorkspaceContext = createContext<WorkspaceContextData | undefined>(undefined);

export function useWorkspace(): WorkspaceContextData {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }

    return workspaceContextData;
}

export function useOrganizationalUnitTemplates(): Array<OrganizationalUnitTemplate> {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }

    return workspaceContextData.organizationalUnitTemplates;
}

export function useWorkspaceUserTemplates(): Array<UserTemplate> {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }

    return workspaceContextData.userTemplates;
}

export function useProductCatalogueTemplates(): Array<ProductCatalogueTemplate> {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }

    return workspaceContextData.productCatalogueTemplates;
}

export function useWorkspaceEventTemplates(): Array<OttopilotEventTemplate> {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }

    return workspaceContextData.eventTemplates;
}

export function useWorkspaceDomainAggregateTemplates(): Array<DomainAggregateTemplate> {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }

    return workspaceContextData.domainAggregateTemplates;
}

export function useWorkspaceEventContainerTemplates(): Array<EventContainerTemplate> {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }

    return workspaceContextData.eventContainerTemplates;
}

export function usePlatformUserDetails(): Array<UserDetails> {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }

    return workspaceContextData.platformUserDetails;
}

export function useUserDefinedObjects(): Array<UserDefinedObject> {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }

    return workspaceContextData.userDefinedObjects;
}

export function useUserDefinedObjectTemplates(): Array<UserDefinedObjectTemplate> {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }

    return workspaceContextData.userDefinedObjectTemplates;
}

export function useUserDefinedObjectOptionsByTemplateId(): ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>> {
    const workspaceContextData = useContext(WorkspaceContext);
    if (workspaceContextData === undefined) {
        throw new Error("WorkspaceContext.Provider not found");
    }
    return workspaceContextData.userDefinedObjectOptionsByTemplateId;
}
export function useEventAdditionalColumns(template: OttopilotEventTemplate | undefined): Array<UserDefinedObjectTemplateAdditionalColumn> {
    const {workspace, userDefinedObjectTemplates} = useWorkspace();
    return useMemo(() => template === undefined ? [] : getEventAdditionalColumns({workspaceId: workspace.id, template, udoTemplates: userDefinedObjectTemplates}),
        [workspace.id, template, userDefinedObjectTemplates]);
}

export function useEventContainerAdditionalColumns(template: EventContainerTemplate | undefined): Array<UserDefinedObjectTemplateAdditionalColumn> {
    const {workspace, userDefinedObjectTemplates} = useWorkspace();
    return useMemo(() => template === undefined ? [] : getEventContainerAdditionalColumns({workspaceId: workspace.id, template, udoTemplates: userDefinedObjectTemplates}),
        [workspace.id, template, userDefinedObjectTemplates]);
}

/** Memoized metadata lookup shared by forms, renderers and their pure helper functions. */
export function useCapabilityUdoColumns(): CapabilityUdoColumnResolver {
    const {workspace, userDefinedObjectTemplates} = useWorkspace();
    return useMemo(() => createCapabilityUdoColumnResolver(workspace.id, userDefinedObjectTemplates), [workspace.id, userDefinedObjectTemplates]);
}

/** Retail templates are composed in the workspace loader from capability settings and all UDO metadata. */
export function useRetailLocationTemplates(): Array<RetailLocationTemplate> {
    return useWorkspace().retailLocationTemplates;
}

export function useRetailLocationTemplateColumns(template: RetailLocationTemplate | undefined): Array<UserDefinedObjectTemplateAdditionalColumn> {
    const {workspace, userDefinedObjectTemplates} = useWorkspace();
    return useMemo(() => template === undefined ? [] : getRetailLocationAdditionalColumns({allUdoTemplates: userDefinedObjectTemplates, template: template, workspaceId: workspace.id}),
        [workspace.id, template, userDefinedObjectTemplates]);
}
